// Homepage Plans Management
// This file manages which 3 plans are displayed on the homepage

// Default plans that will show if no admin plans are set
export const defaultHomepagePlans = [
  {
    id: 'super-deal-500-default',
    name: 'Super Deal NBN 500/100 Mbps',
    price: 0.1,
    originalPrice: 0.1,
    speed: '500/100 Mbps',
    data: 'Unlimited',
    features: ['Ultra-fast download speeds', 'Perfect for live testing', 'No lock-in contract', 'Priority support', 'Future-proof technology'],
    popular: true,
    isNew: true,
    hasPromo: true,
    promoCode: 'LIVETEST',
    discountValue: 99.9,
    color: 'from-[#FFD700] to-[#FFA500]',
    category: 'super'
  },
  {
    id: 'nbn100-default',
    name: 'NBN100 FTTP Limitless',
    price: 94.99,
    originalPrice: 94.99,
    speed: '100/20 Mbps',
    data: 'Unlimited',
    features: ['Fibre to the Premises', 'No lock-in contract', 'Free setup', 'Priority support'],
    popular: false,
    isNew: false,
    hasPromo: false,
    promoCode: null,
    discountValue: null,
    color: 'from-blue-500 to-blue-600',
    category: 'standard'
  },
  {
    id: 'premium-150-default',
    name: 'Premium Fibre 150 Mbps',
    price: 89.99,
    originalPrice: 89.99,
    speed: '150/30 Mbps',
    data: 'Unlimited',
    features: ['Ultra-fast Fibre', 'Priority support', 'Free modem', '4K streaming ready'],
    popular: false,
    isNew: true,
    hasPromo: true,
    promoCode: 'SAVE10',
    discountValue: 10,
    color: 'from-[#FFD700] to-[#FFB700]',
    category: 'premium'
  }
];

// Admin-managed plans (this would come from your admin panel)
export const adminHomepagePlans = [
  {
    id: 'basic-50',
    name: 'Basic NBN 50 Mbps',
    price: 59.99,
    originalPrice: 59.99,
    speed: '50 Mbps',
    data: 'Unlimited',
    features: ['Perfect for small households', 'Stream HD content', 'Basic gaming', 'Email & web browsing'],
    isPopular: false,
    isNew: false,
    hasPromo: false,
    promoCode: null,
    discountValue: null,
    color: 'from-green-500 to-green-600',
    category: 'basic',
    showOnHomepage: true,
    sortOrder: 1
  },
  {
    id: 'nbn100',
    name: 'NBN100 FTTP Limitless',
    price: 94.99,
    originalPrice: 94.99,
    speed: '100 Mbps',
    data: 'Unlimited',
    features: ['Ideal for families', '4K streaming', 'Online gaming', 'Multiple devices', 'Unlimited data'],
    isPopular: true,
    isNew: false,
    hasPromo: false,
    promoCode: null,
    discountValue: null,
    color: 'from-blue-500 to-blue-600',
    category: 'standard',
    showOnHomepage: true,
    sortOrder: 2
  },
  {
    id: 'premium-150',
    name: 'Premium Fibre 150 Mbps',
    price: 89.99,
    originalPrice: 89.99,
    speed: '150 Mbps',
    data: 'Unlimited',
    features: ['Perfect for power users', '8K streaming', 'Competitive gaming', 'Home office', 'Unlimited data'],
    isPopular: false,
    isNew: true,
    hasPromo: true,
    promoCode: 'SAVE10',
    discountValue: 10,
    color: 'from-[#FFD700] to-[#FFB700]',
    category: 'premium',
    showOnHomepage: true,
    sortOrder: 3
  },
  {
    id: 'ultra-250',
    name: 'Ultra Fibre 250 Mbps',
    price: 109.99,
    originalPrice: 109.99,
    speed: '250 Mbps',
    data: 'Unlimited',
    features: ['Ultra-fast speeds', 'Multiple 4K streams', 'Professional gaming', 'Large households', 'Unlimited data'],
    isPopular: false,
    isNew: false,
    hasPromo: false,
    promoCode: null,
    discountValue: null,
    color: 'from-purple-500 to-purple-600',
    category: 'ultra',
    showOnHomepage: false, // Hidden from homepage
    sortOrder: 4
  },
  {
    id: 'gigabit-1000',
    name: 'Gigabit Fibre 1000 Mbps',
    price: 149.99,
    originalPrice: 149.99,
    speed: '1000 Mbps',
    data: 'Unlimited',
    features: ['Lightning fast', 'Future-proof', 'Professional use', 'Multiple users', 'Unlimited data'],
    isPopular: false,
    isNew: false,
    hasPromo: false,
    promoCode: null,
    discountValue: null,
    color: 'from-red-500 to-red-600',
    category: 'gigabit',
    showOnHomepage: true,
    sortOrder: 5
  },
  {
    id: 'super-deal-500',
    name: 'Super Deal NBN 500/100 Mbps',
    price: 0.1,
    originalPrice: 0.1,
    speed: '500/100 Mbps',
    data: 'Unlimited',
    features: ['Ultra-fast download speeds', 'Perfect for live testing', 'No lock-in contract', 'Priority support', 'Future-proof technology'],
    isPopular: true,
    isNew: true,
    hasPromo: true,
    promoCode: 'LIVETEST',
    discountValue: 99.9,
    color: 'from-[#FFD700] to-[#FFA500]',
    category: 'super',
    showOnHomepage: true,
    sortOrder: 0
  }
];

/**
 * Get the 3 plans that should be displayed on the homepage
 * @returns {Array} - Array of 3 plans for homepage display
 */
export const getHomepagePlans = () => {
  // Load saved visibility settings from localStorage
  let savedSettings = {};
  try {
    savedSettings = JSON.parse(localStorage.getItem('adminHomepagePlans') || '{}');
  } catch (error) {
    console.error('Error loading saved plan settings:', error);
  }
  
  // Force Super Deal plan to be visible for testing
  if (savedSettings['super-deal-500'] === undefined) {
    savedSettings['super-deal-500'] = { showOnHomepage: true };
    localStorage.setItem('adminHomepagePlans', JSON.stringify(savedSettings));
  }
  
  // Apply saved settings to plans
  const plansWithSettings = adminHomepagePlans.map(plan => ({
    ...plan,
    showOnHomepage: savedSettings[plan.id]?.showOnHomepage !== undefined 
      ? savedSettings[plan.id].showOnHomepage 
      : plan.showOnHomepage
  }));
  
  // Get plans that are marked to show on homepage
  const availablePlans = plansWithSettings.filter(plan => plan.showOnHomepage);
  
  // Sort by priority: Popular first, then by sort order (lower sortOrder = higher priority)
  const sortedPlans = availablePlans.sort((a, b) => {
    if (a.isPopular && !b.isPopular) return -1;
    if (!a.isPopular && b.isPopular) return 1;
    return a.sortOrder - b.sortOrder;
  });
  
  // Return top 3 plans, or default plans if no admin plans are available
  if (sortedPlans.length >= 3) {
    return sortedPlans.slice(0, 3);
  } else if (sortedPlans.length > 0) {
    // If we have some admin plans but less than 3, fill with defaults
    const remainingSlots = 3 - sortedPlans.length;
    return [...sortedPlans, ...defaultHomepagePlans.slice(0, remainingSlots)];
  } else {
    // If no admin plans are set to show, use defaults
    return defaultHomepagePlans;
  }
};

/**
 * Update a plan's homepage visibility
 * @param {string} planId - The plan ID to update
 * @param {boolean} showOnHomepage - Whether to show on homepage
 */
export const updatePlanHomepageVisibility = (planId, showOnHomepage) => {
  const planIndex = adminHomepagePlans.findIndex(plan => plan.id === planId);
  if (planIndex !== -1) {
    adminHomepagePlans[planIndex].showOnHomepage = showOnHomepage;
    
    // Save to localStorage for persistence
    try {
      const savedPlans = JSON.parse(localStorage.getItem('adminHomepagePlans') || '{}');
      savedPlans[planId] = { showOnHomepage };
      localStorage.setItem('adminHomepagePlans', JSON.stringify(savedPlans));
      console.log('Plan homepage visibility updated and saved');
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }
};

/**
 * Add a new plan to the system
 * @param {Object} plan - Plan object to add
 */
export const addPlan = (plan) => {
  adminHomepagePlans.push(plan);
  savePlansToStorage();
};

/**
 * Update an existing plan
 * @param {string} planId - Plan ID to update
 * @param {Object} updatedPlan - Updated plan object
 */
export const updatePlan = (planId, updatedPlan) => {
  const planIndex = adminHomepagePlans.findIndex(plan => plan.id === planId);
  if (planIndex !== -1) {
    adminHomepagePlans[planIndex] = { ...adminHomepagePlans[planIndex], ...updatedPlan };
    savePlansToStorage();
  }
};

/**
 * Delete a plan from the system
 * @param {string} planId - Plan ID to delete
 */
export const deletePlan = (planId) => {
  const planIndex = adminHomepagePlans.findIndex(plan => plan.id === planId);
  if (planIndex !== -1) {
    adminHomepagePlans.splice(planIndex, 1);
    savePlansToStorage();
  }
};

/**
 * Save all plans to localStorage for persistence
 */
const savePlansToStorage = () => {
  try {
    localStorage.setItem('adminHomepagePlansData', JSON.stringify(adminHomepagePlans));
    console.log('Plans saved successfully');
  } catch (error) {
    console.error('Error saving plans to localStorage:', error);
  }
};

/**
 * Load plans from localStorage
 */
const loadPlansFromStorage = () => {
  try {
    const savedPlans = localStorage.getItem('adminHomepagePlansData');
    if (savedPlans) {
      const parsedPlans = JSON.parse(savedPlans);
      adminHomepagePlans.length = 0; // Clear array
      adminHomepagePlans.push(...parsedPlans); // Add saved plans
      console.log('Plans loaded successfully');
    }
  } catch (error) {
    console.error('Error loading plans from localStorage:', error);
  }
};

// Load plans from storage on module initialization (only in browser environment)
if (typeof window !== 'undefined') {
  loadPlansFromStorage();
}

/**
 * Get all available plans for admin management
 * @returns {Array} - All plans with their homepage visibility status
 */
export const getAllPlans = () => {
  // Load saved visibility settings from localStorage
  let savedSettings = {};
  try {
    savedSettings = JSON.parse(localStorage.getItem('adminHomepagePlans') || '{}');
  } catch (error) {
    console.error('Error loading saved plan settings:', error);
  }
  
  // Force Super Deal plan to be visible for testing
  if (savedSettings['super-deal-500'] === undefined) {
    savedSettings['super-deal-500'] = { showOnHomepage: true };
    localStorage.setItem('adminHomepagePlans', JSON.stringify(savedSettings));
  }
  
  // Apply saved settings to plans
  return adminHomepagePlans.map(plan => ({
    ...plan,
    showOnHomepage: savedSettings[plan.id]?.showOnHomepage !== undefined 
      ? savedSettings[plan.id].showOnHomepage 
      : plan.showOnHomepage
  }));
};

/**
 * Force refresh plans data (clears cache and reloads)
 */
export const forceRefreshPlans = () => {
  // Clear localStorage cache
  localStorage.removeItem('adminHomepagePlans');
  localStorage.removeItem('adminHomepagePlansData');
  
  // Force Super Deal plan to be visible
  const savedSettings = {
    'super-deal-500': { showOnHomepage: true }
  };
  localStorage.setItem('adminHomepagePlans', JSON.stringify(savedSettings));
  
  console.log('Plans data refreshed - Super Deal plan should now be visible');
};

/**
 * Get all available plans for plan upgrades (includes all plans regardless of homepage visibility)
 * @returns {Array} - All plans available for upgrades/downgrades
 */
export const getAllUpgradePlans = () => {
  // Load saved visibility settings from localStorage
  let savedSettings = {};
  try {
    savedSettings = JSON.parse(localStorage.getItem('adminHomepagePlans') || '{}');
  } catch (error) {
    console.error('Error loading saved plan settings:', error);
  }
  
  // Return all plans with their current settings, but mark all as available for upgrades
  return adminHomepagePlans.map(plan => ({
    ...plan,
    showOnHomepage: savedSettings[plan.id]?.showOnHomepage !== undefined 
      ? savedSettings[plan.id].showOnHomepage 
      : plan.showOnHomepage,
    isActive: true // All plans are active for upgrades
  }));
};

/**
 * Get plan statistics
 * @returns {Object} - Plan statistics
 */
export const getPlanStats = () => {
  const totalPlans = adminHomepagePlans.length;
  const homepagePlans = adminHomepagePlans.filter(plan => plan.showOnHomepage).length;
  const popularPlans = adminHomepagePlans.filter(plan => plan.isPopular).length;
  const newPlans = adminHomepagePlans.filter(plan => plan.isNew).length;
  const promotionalPlans = adminHomepagePlans.filter(plan => plan.hasPromo).length;

  return {
    total: totalPlans,
    onHomepage: homepagePlans,
    popular: popularPlans,
    new: newPlans,
    promotional: promotionalPlans
  };
};
